import * as ActionType from "./course.type";

// Helper to compute total duration from lessons
const computeCourseDuration = (lessons = []) => {
  return lessons.reduce((sum, lesson) => {
    const value = Number(lesson?.duration || 0);
    return sum + (isNaN(value) ? 0 : value);
  }, 0);
};

const initialState = {
  courseData: [],
  totalCourses: 0,
  selectedCourse: null,
};

export const courseReducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionType.GET_COURSES:
      return {
        ...state,
        courseData: action.payload.course,
        totalCourses: action.payload.totalCourses,
      };

    case ActionType.ADD_COURSE:
      return {
        ...state,
        courseData: [action.payload, ...state.courseData],
        selectedCourse: action.payload,
        totalCourses: state.totalCourses + 1,
      };

    case ActionType.UPDATE_COURSE:
      const { courseId, updatedData } = action.payload;
      return {
        ...state,
        courseData: state.courseData.map((course) =>
          course._id === courseId ? { ...course, ...updatedData } : course,
        ),
      };

    case ActionType.DELETE_COURSE:
      return {
        ...state,
        courseData: state.courseData.filter(
          (course) => course._id !== action.payload,
        ),
        totalCourses: state.totalCourses - 1,
      };

    case ActionType.UPDATE_COURSE_ACCESS: {
      const { courseId, updatedData } = action.payload;

      const courseData = state.courseData.map((course) => {
        return course._id === courseId ? { ...course, ...updatedData } : course;
      });

      let selectedCourse = state.selectedCourse;
      selectedCourse =
        state.selectedCourse && state.selectedCourse._id === courseId
          ? { ...state.selectedCourse, ...updatedData }
          : state.selectedCourse;

      return {
        ...state,
        courseData,
        selectedCourse: selectedCourse,
      };
    }

    case ActionType.ADD_LESSON: {
      const { courseId: addCourseId, lessonData } = action.payload;
      const updatedCourseData = state.courseData.map((course) => {
        if (course._id !== addCourseId) return course;
        const newLessons = [...(course.lessons || []), lessonData];
        return {
          ...course,
          lessons: newLessons,
          duration: computeCourseDuration(newLessons),
        };
      });

      let updatedSelectedCourse = state.selectedCourse;
      if (state.selectedCourse && state.selectedCourse._id === addCourseId) {
        const newLessons = [
          ...(state.selectedCourse.lessons || []),
          lessonData,
        ];
        updatedSelectedCourse = {
          ...state.selectedCourse,
          lessons: newLessons,
          duration: computeCourseDuration(newLessons),
        };
      }

      return {
        ...state,
        courseData: updatedCourseData,
        selectedCourse: updatedSelectedCourse,
      };
    }

    case ActionType.UPDATE_LESSON: {
      const {
        courseId: updateCourseId,
        lessonId,
        updatedData: lessonUpdatedData,
      } = action.payload;

      const updatedCourseData = state.courseData.map((course) => {
        if (course._id !== updateCourseId) return course;
        const newLessons = (course.lessons || []).map((lesson) =>
          lesson._id === lessonId
            ? { ...lesson, ...lessonUpdatedData }
            : lesson,
        );
        return {
          ...course,
          lessons: newLessons,
          duration: computeCourseDuration(newLessons),
        };
      });

      let updatedSelectedCourse = state.selectedCourse;
      if (state.selectedCourse && state.selectedCourse._id === updateCourseId) {
        const newLessons = (state.selectedCourse.lessons || []).map((lesson) =>
          lesson._id === lessonId
            ? { ...lesson, ...lessonUpdatedData }
            : lesson,
        );
        updatedSelectedCourse = {
          ...state.selectedCourse,
          lessons: newLessons,
          duration: computeCourseDuration(newLessons),
        };
      }

      return {
        ...state,
        courseData: updatedCourseData,
        selectedCourse: updatedSelectedCourse,
      };
    }

    case ActionType.DELETE_LESSON: {
      const { courseId: deleteCourseId, lessonId: deleteLessonId } =
        action.payload;

      const updatedCourseData = state.courseData.map((course) => {
        if (course._id !== deleteCourseId) return course;
        const newLessons = (course.lessons || []).filter(
          (lesson) => lesson._id !== deleteLessonId,
        );
        return {
          ...course,
          lessons: newLessons,
          duration: computeCourseDuration(newLessons),
        };
      });

      let updatedSelectedCourse = state.selectedCourse;
      if (state.selectedCourse && state.selectedCourse._id === deleteCourseId) {
        const newLessons = (state.selectedCourse.lessons || []).filter(
          (lesson) => lesson._id !== deleteLessonId,
        );
        updatedSelectedCourse = {
          ...state.selectedCourse,
          lessons: newLessons,
          duration: computeCourseDuration(newLessons),
        };
      }

      return {
        ...state,
        courseData: updatedCourseData,
        selectedCourse: updatedSelectedCourse,
      };
    }

    case ActionType.CREATE_QUIZ:
      const {
        courseId: createQuizCourseId,
        lessonId: createQuizLessonId,
        initialQuizData,
      } = action.payload;
      return {
        ...state,
        courseData: state.courseData.map((course) =>
          course._id === createQuizCourseId
            ? {
                ...course,
                lessons: course.lessons.map((lesson) =>
                  lesson._id === createQuizLessonId
                    ? {
                        ...lesson,
                        quiz: { ...lesson.quiz, ...initialQuizData },
                      }
                    : lesson,
                ),
              }
            : course,
        ),
        selectedCourse:
          state.selectedCourse &&
          state.selectedCourse._id === createQuizCourseId
            ? {
                ...state.selectedCourse,
                lessons: state.selectedCourse.lessons.map((lesson) =>
                  lesson._id === createQuizLessonId
                    ? {
                        ...lesson,
                        quiz: { ...lesson.quiz, ...initialQuizData },
                      }
                    : lesson,
                ),
              }
            : state.selectedCourse,
      };

    case ActionType.ADD_QUIZ_QUESTION:
      const {
        courseId: quizCourseId,
        lessonId: quizLessonId,
        quizData,
      } = action.payload;
      return {
        ...state,
        courseData: state.courseData.map((course) =>
          course._id === quizCourseId
            ? {
                ...course,
                lessons: course.lessons.map((lesson) =>
                  lesson._id === quizLessonId
                    ? {
                        ...lesson,
                        quiz: lesson.quiz
                          ? [...lesson.quiz, quizData]
                          : [quizData],
                      }
                    : lesson,
                ),
              }
            : course,
        ),
        selectedCourse:
          state.selectedCourse && state.selectedCourse._id === quizCourseId
            ? {
                ...state.selectedCourse,
                lessons: state.selectedCourse.lessons.map((lesson) =>
                  lesson._id === quizLessonId
                    ? {
                        ...lesson,
                        quiz: lesson.quiz
                          ? [...lesson.quiz, quizData]
                          : [quizData],
                      }
                    : lesson,
                ),
              }
            : state.selectedCourse,
      };

    case ActionType.UPDATE_QUIZ_QUESTION:
      const {
        courseId: updateQuizCourseId,
        lessonId: updateQuizLessonId,
        updatedQuizData,
      } = action.payload;
      return {
        ...state,
        courseData: state.courseData.map((course) =>
          course._id === updateQuizCourseId
            ? {
                ...course,
                lessons: course.lessons.map((lesson) =>
                  lesson._id === updateQuizLessonId && lesson.quiz
                    ? {
                        ...lesson,
                        quiz: updatedQuizData,
                      }
                    : lesson,
                ),
              }
            : course,
        ),
        selectedCourse:
          state.selectedCourse &&
          state.selectedCourse._id === updateQuizCourseId
            ? {
                ...state.selectedCourse,
                lessons: state.selectedCourse.lessons.map((lesson) =>
                  lesson._id === updateQuizLessonId && lesson.quiz
                    ? {
                        ...lesson,
                        quiz: updatedQuizData,
                      }
                    : lesson,
                ),
              }
            : state.selectedCourse,
      };

    case ActionType.DELETE_QUIZ_QUESTION:
      const {
        courseId: deleteQuizCourseId,
        lessonId: deleteQuizLessonId,
        updatedQuiz,
      } = action.payload;
      return {
        ...state,
        courseData: state.courseData.map((course) =>
          course._id === deleteQuizCourseId
            ? {
                ...course,
                lessons: course.lessons.map((lesson) =>
                  lesson._id === deleteQuizLessonId && lesson.quiz
                    ? {
                        ...lesson,
                        quiz: updatedQuiz,
                      }
                    : lesson,
                ),
              }
            : course,
        ),
        selectedCourse:
          state.selectedCourse &&
          state.selectedCourse._id === deleteQuizCourseId
            ? {
                ...state.selectedCourse,
                lessons: state.selectedCourse.lessons.map((lesson) =>
                  lesson._id === deleteQuizLessonId && lesson.quiz
                    ? {
                        ...lesson,
                        quiz: updatedQuiz,
                      }
                    : lesson,
                ),
              }
            : state.selectedCourse,
      };

    case ActionType.TOGGLE_PUBLISH_STATUS:
      const { courseId: togglePublishCourseId } = action.payload;
      return {
        ...state,
        courseData: state.courseData.map((course) =>
          course._id === togglePublishCourseId
            ? { ...course, isPublished: !course.isPublished }
            : course,
        ),
        selectedCourse:
          state.selectedCourse &&
          state.selectedCourse._id === togglePublishCourseId
            ? {
                ...state.selectedCourse,
                isPublished: !state.selectedCourse.isPublished,
              }
            : state.selectedCourse,
      };

    case ActionType.SELECT_COURSE:
      return {
        ...state,
        selectedCourse: action.payload,
      };

    case ActionType.TOGGLE_COURSE_VISIBILITY:
      const { courseId: toggleVisibilityCourseId } = action.payload;
      return {
        ...state,
        courseData: state.courseData.map((course) =>
          course._id === toggleVisibilityCourseId
            ? { ...course, isPublic: !course.isPublic }
            : course,
        ),
      };

    default:
      return state;
  }
};
